import PropTypes from "prop-types"
import React, { useState } from "react"

import { formatDurationWithSessions } from "../../../utils/utils"
import { formatFontFamily } from "../utils/fontUtils"
import { normalizeWebsiteLocationKind } from "../utils/locationKindUtils"

// Icons for the search bar
const TimeIcon = ({ theme, isMobile = false }) => (
  <svg className={`${isMobile ? "size-5" : "size-8"}`} viewBox="0 0 32 32" fill="none">
    <path
      d="M12.75 4.75V12.75H18.75M24.75 12.75C24.75 14.3259 24.4396 15.8863 23.8366 17.3422C23.2335 18.7981 22.3496 20.121 21.2353 21.2353C20.121 22.3496 18.7981 23.2335 17.3422 23.8366C15.8863 24.4396 14.3259 24.75 12.75 24.75C11.1741 24.75 9.61371 24.4396 8.1578 23.8366C6.70189 23.2335 5.37902 22.3496 4.26472 21.2353C3.15042 20.121 2.2665 18.7981 1.66345 17.3422C1.06039 15.8863 0.75 14.3259 0.75 12.75C0.75 9.5674 2.01428 6.51516 4.26472 4.26472C6.51516 2.01428 9.5674 0.75 12.75 0.75C15.9326 0.75 18.9848 2.01428 21.2353 4.26472C23.4857 6.51516 24.75 9.5674 24.75 12.75Z"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
  </svg>
)

const MapPinIcon = ({ theme, isMobile = false }) => (
  <svg className={`${isMobile ? "size-5" : "size-8"}`} viewBox="0 0 32 32" fill="none">
    <path
      d="M20.75 10.75C20.75 20.2727 10.75 25.75 10.75 25.75C10.75 25.75 0.75 20.2727 0.75 10.75C0.75 8.09784 1.80357 5.5543 3.67893 3.67893C5.5543 1.80357 8.09784 0.75 10.75 0.75C13.4022 0.75 15.9457 1.80357 17.8211 3.67893C19.6964 5.5543 20.75 8.09784 20.75 10.75Z"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
    />
    {/* Circle centered in the pin's round part (10.75, 10.75 in viewBox) */}
    <circle
      cx="10.75"
      cy="10.75"
      r="3"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
)

const ChevronDownIcon = ({ theme, isMobile = false }) => (
  <svg className={`${isMobile ? "size-4" : "size-6"}`} viewBox="0 0 24 24" fill="none">
    <path
      d="M6 9l6 6 6-6"
      stroke={theme?.colors?.textDark || "#55433f"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ServiceTimeSearchBar = ({
  service,
  selectedDuration,
  selectedLocation,
  onDurationChange,
  onLocationChange,
  hasMultipleDurations,
  hasMultipleLocations,
  theme,
  deviceType = "desktop"
}) => {
  const [showDurationMenu, setShowDurationMenu] = useState(false)
  const [showLocationMenu, setShowLocationMenu] = useState(false)

  const formatPrice = (amountCents) => {
    if (!amountCents || amountCents === 0) return "Free"
    return `$${(amountCents / 100).toFixed(0)}`
  }

  const formatServiceDuration = (timeLength) => {
    if (!timeLength) return ""
    return formatDurationWithSessions(timeLength, service)
  }

  // For in-person (office/physical): primary = City, ST (big), secondary = street address (small).
  // For virtual/home: single primary line, optional secondary label.
  const getLocationDisplayLines = (location) => {
    if (!location) return { primary: "Select location", secondary: null }

    const kind = normalizeWebsiteLocationKind(location.kind)
    const stateAbbr = location.stateAbbreviation ?? location.state_abbreviation
    const secondaryAddr = location.secondaryAddress ?? location.secondary_address

    if (kind === "virtual") {
      return { primary: "Virtual", secondary: "Online" }
    }
    if (kind === "home") {
      return { primary: "At your home", secondary: "In-home" }
    }
    if (kind === "office" || kind === "physical") {
      const hideAddr = location.hide_address || location.hideAddress
      const cityState =
        location.city && (stateAbbr || location.state) ? `${location.city}, ${stateAbbr || location.state}` : null

      // When address is hidden until booking, never reveal the street address here.
      if (hideAddr) {
        return {
          primary: cityState || "In this area",
          secondary: "Address provided after booking"
        }
      }

      // In-person with visible address: City, ST (primary) and street address (secondary)
      const streetAddress =
        location.address && location.address.trim()
          ? [location.address, secondaryAddr].filter(Boolean).join(", ")
          : null
      if (cityState || streetAddress) {
        return {
          primary: cityState || streetAddress || "In office",
          secondary: cityState && streetAddress ? streetAddress : null
        }
      }
      return { primary: "In office", secondary: "In-office" }
    }
    // Fallback
    const hideAddr = location.hide_address || location.hideAddress
    if (hideAddr) return { primary: location.name || "Location", secondary: "Location" }
    const cityState =
      location.city && (stateAbbr || location.state) ? `${location.city}, ${stateAbbr || location.state}` : null
    const street = location.address ? [location.address, secondaryAddr].filter(Boolean).join(", ") : null
    return {
      primary: cityState || street || "Location",
      secondary: cityState && street ? street : null
    }
  }

  const handleDurationSelect = (duration) => {
    onDurationChange(duration)
    setShowDurationMenu(false)
  }

  const handleLocationSelect = (location) => {
    onLocationChange(location)
    setShowLocationMenu(false)
  }

  // Get duration options (main service + variations)
  const durationOptions = [
    {
      id: service.id,
      name: service.name,
      amountCents: service.amountCents,
      timeLength: service.timeLength
    },
    ...(service.variations || [])
  ]

  // Determine layout based on deviceType
  const isMobile = deviceType === "mobile"

  return (
    <div
      className={`box-border flex w-full gap-1.5 rounded-xl p-2 shadow-[0px_8px_9px_0px_rgba(0,0,0,0.07)] ${
        isMobile ? "flex-col" : "h-32 max-w-[872px] flex-row gap-4 p-6"
      }`}
      style={{ backgroundColor: "#FFFFFF" }}>
      {/* Duration Selector */}
      <div className={`relative ${isMobile ? "w-full" : "w-1/2"}`}>
        <div
          className={`box-border flex w-full items-center justify-start gap-1.5 border ${
            isMobile ? "rounded-md px-2 py-1" : "gap-4 rounded-xl px-5 py-3"
          } ${hasMultipleDurations ? "cursor-pointer" : "cursor-default"}`}
          style={{
            borderColor: theme?.colors?.greyLight || "#e6e6e6",
            backgroundColor: "#FFFFFF"
          }}
          onClick={() => hasMultipleDurations && setShowDurationMenu(!showDurationMenu)}>
          <div className={`flex flex-1 items-center justify-start ${isMobile ? "gap-1.5" : "gap-4"}`}>
            <TimeIcon theme={theme} isMobile={isMobile} />
            <div
              className="flex flex-1 flex-col items-start justify-start"
              style={{ color: theme?.colors?.textDark || "#55433f" }}>
              <div
                className={`w-full font-semibold ${isMobile ? "text-xs leading-4" : "text-[20px] leading-[32px]"}`}
                style={{ fontFamily: formatFontFamily(theme?.fonts?.paragraph) }}>
                {selectedDuration ? formatServiceDuration(selectedDuration.timeLength) : "60 mins"}
              </div>
              <div
                className={`w-full font-normal ${isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"}`}
                style={{ fontFamily: formatFontFamily(theme?.fonts?.paragraph) }}>
                {selectedDuration ? formatPrice(selectedDuration.amountCents) : formatPrice(service.amountCents)}
              </div>
            </div>
          </div>
          {hasMultipleDurations && <ChevronDownIcon theme={theme} isMobile={isMobile} />}
        </div>

        {/* Duration Dropdown */}
        {showDurationMenu && hasMultipleDurations && (
          <div
            className={`absolute left-0 top-full z-10 mt-1 w-full border shadow-lg ${
              isMobile ? "rounded-md px-1 py-1.5" : "rounded-lg px-2 py-4"
            }`}
            style={{
              borderColor: theme?.colors?.greyLight || "#e6e6e6",
              backgroundColor: "#FFFFFF"
            }}>
            {durationOptions.map((duration) => (
              <div
                key={duration.id}
                className={`flex cursor-pointer items-center justify-start rounded ${
                  isMobile ? "gap-1 px-1.5 py-1" : "gap-4 px-4 py-2"
                } ${selectedDuration?.id === duration.id ? "bg-[#f8f6f5]" : "hover:bg-[#f8f6f5]"}`}
                onClick={() => handleDurationSelect(duration)}>
                <TimeIcon theme={theme} isMobile={isMobile} />
                <div
                  className="flex flex-1 flex-col items-start justify-start"
                  style={{ color: theme?.colors?.textDark || "#55433f" }}>
                  <div
                    className={`font-semibold ${isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"}`}
                    style={{ fontFamily: formatFontFamily(theme?.fonts?.paragraph) }}>
                    {formatServiceDuration(duration.timeLength)}
                  </div>
                  <div
                    className={`font-normal ${isMobile ? "text-[8px]" : "text-[14px] leading-[20px]"}`}
                    style={{ fontFamily: formatFontFamily(theme?.fonts?.paragraph) }}>
                    {formatPrice(duration.amountCents)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Location Selector */}
      <div className={`relative ${isMobile ? "w-full" : "w-1/2"}`}>
        <div
          className={`box-border flex w-full items-center justify-start gap-1.5 border ${
            isMobile ? "rounded-md px-2 py-1" : "gap-4 rounded-xl px-5 py-3"
          } ${hasMultipleLocations ? "cursor-pointer" : "cursor-default"}`}
          style={{
            borderColor: theme?.colors?.greyLight || "#e6e6e6",
            backgroundColor: "#FFFFFF"
          }}
          onClick={() => hasMultipleLocations && setShowLocationMenu(!showLocationMenu)}>
          <div className={`flex min-w-0 flex-1 items-center justify-start ${isMobile ? "gap-1.5" : "gap-4"}`}>
            <MapPinIcon theme={theme} isMobile={isMobile} />
            <div
              className="flex min-w-0 flex-1 flex-col items-start justify-start overflow-hidden"
              style={{ color: theme?.colors?.textDark || "#55433f" }}>
              {(() => {
                const lines = getLocationDisplayLines(selectedLocation)
                return (
                  <>
                    <div
                      className={`w-full font-semibold ${
                        isMobile ? "text-xs leading-4" : "text-[20px] leading-[32px]"
                      }`}
                      style={{
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%"
                      }}>
                      {lines.primary}
                    </div>
                    {lines.secondary != null && (
                      <div
                        className={`w-full truncate font-normal ${
                          isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"
                        }`}
                        style={{
                          fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                          color: theme?.colors?.textGrey || "#666666"
                        }}>
                        {lines.secondary}
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          </div>
          {hasMultipleLocations && <ChevronDownIcon theme={theme} isMobile={isMobile} />}
        </div>

        {/* Location Dropdown */}
        {showLocationMenu && hasMultipleLocations && (
          <div
            className={`absolute left-0 top-full z-10 mt-1 w-full border shadow-lg ${
              isMobile ? "rounded-md px-1 py-1.5" : "rounded-lg px-2 py-4"
            }`}
            style={{
              borderColor: theme?.colors?.greyLight || "#e6e6e6",
              backgroundColor: "#FFFFFF"
            }}>
            {service.locations.map((location) => {
              const lines = getLocationDisplayLines(location)
              return (
                <div
                  key={location.id}
                  className={`flex cursor-pointer items-center justify-start rounded ${
                    isMobile ? "gap-1 px-1.5 py-1" : "gap-4 px-4 py-2"
                  } ${selectedLocation?.id === location.id ? "bg-[#f8f6f5]" : "hover:bg-[#f8f6f5]"}`}
                  onClick={() => handleLocationSelect(location)}>
                  <MapPinIcon theme={theme} isMobile={isMobile} />
                  <div
                    className="flex flex-1 flex-col items-start justify-start"
                    style={{ color: theme?.colors?.textDark || "#55433f" }}>
                    <div
                      className={`w-full font-semibold ${
                        isMobile ? "text-[10px] leading-3" : "text-[16px] leading-[24px]"
                      }`}
                      style={{
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        wordWrap: "break-word",
                        overflowWrap: "break-word"
                      }}>
                      {lines.primary}
                    </div>
                    {lines.secondary != null && (
                      <div
                        className={`w-full font-normal ${isMobile ? "text-[8px]" : "text-[14px] leading-[20px]"}`}
                        style={{
                          fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                          color: theme?.colors?.textGrey || "#666666"
                        }}>
                        {lines.secondary}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

ServiceTimeSearchBar.propTypes = {
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    amountCents: PropTypes.number,
    timeLength: PropTypes.number,
    variations: PropTypes.arrayOf(PropTypes.object),
    locations: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  selectedDuration: PropTypes.object,
  selectedLocation: PropTypes.object,
  onDurationChange: PropTypes.func.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  hasMultipleDurations: PropTypes.bool.isRequired,
  hasMultipleLocations: PropTypes.bool.isRequired,
  theme: PropTypes.object,
  deviceType: PropTypes.string
}

export default ServiceTimeSearchBar
